<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { useUnifiedPublicLoaderContext } from '~kui/composables/useUnifiedPublicLoaderContext';
import { AnimatePresence, motion } from 'motion-v';

import SpotlightHeroAnimation from '~/components/about/SpotlightHeroAnimation.vue';
import SpotlightImage from '~/components/about/SpotlightImage.vue';
import { useSignupLink } from '~/composables/unified/useSignupLink';
import { ENTRY_CTA, ENTRY_SOURCE } from '~/utils/unified/entryTracking';

const loaderCtx = useUnifiedPublicLoaderContext();

const { buildLink } = useSignupLink();
const loginLink = computed(() => buildLink({ entrySource: ENTRY_SOURCE.ROOT_UNIFIED, entryCta: ENTRY_CTA.HERO_CTA }));
</script>

<template>
  <SpotlightHeroAnimation
    :trigger="unref(loaderCtx?.loaderGone)"
  >
    <div class="relative max-w-2xl mx-auto -mt-10 lg:-mt-30">
      <SpotlightImage
        class="absolute left-0 top-20 lg:-left-10 lg:top-35 z-30"
        src="/images/illustrations/suitcase.webp"
        image-class="aspect-[0.75] w-30 lg:w-40"
        :label="$t('unified.landing.hero.careers.cabinCrew')"
      />
      <SpotlightImage
        class="absolute -right-2 lg:right-30 -top-25 z-30"
        src="/images/illustrations/cream.webp"
        image-class="w-35 lg:w-45 aspect-square"
        :label="$t('unified.landing.hero.careers.baker')"
      />
      <SpotlightImage
        class="absolute top-45 -right-0 md:top-30 lg:top-45 lg:-right-10 z-30"
        src="/images/illustrations/boxinggloves.webp"
        image-class="w-35 lg:w-45 aspect-square"
        :label="$t('unified.landing.hero.careers.professionalAthlete')"
      />
    </div>
    <div class=" max-w-2xl mx-auto">
      <Heading
        size="3xl"
        as="h1"
        class="text-center tracking-tight !text-[5.4rem] md:!text-[7.5rem] lg:!text-[9rem] !leading-none lg:mb-8 mb-4"
      >
        {{ $t('unified.landing.hero.heading') }}
      </Heading>
      <Heading
        size="md"
        as="h2"
        weight="medium"
        class="max-w-[24ch] md:max-w-none text-center tracking-tight leading-[1.2] mx-auto my-2"
      >
        {{ $t('unified.landing.hero.subheading') }}
      </Heading>
      <Text
        class="my-2 text-center"
        weight="normal"
      >
        {{ $t('unified.landing.hero.description') }}
      </Text>
      <div class="text-center">
        <Button
          id="cta"
          variant="secondary"
          size="xl"
          class="mt-8"
          as-child
        >
          <NuxtLink :to="loginLink">
            {{ $t('unified.landing.hero.cta') }}
          </NuxtLink>
        </Button>
      </div>
    </div>

    <div
      class="absolute top-full left-1/2 -translate-x-1/2 mt-16 lg:mt-8 flex flex-col items-center gap-1"
    >
      <span class="text-xs font-semibold tracking-widest uppercase">{{ $t('unified.landing.hero.scroll') }}</span>
      <AnimatePresence mode="wait">
        <motion.div
          :animate="{ y: [0, 10, 0] }"
          :transition="{ duration: 1.8, ease: 'easeInOut', repeat: Infinity }"
        >
          <Icon :name="ICONS.chevronDoubleDown" size="lg" />
        </motion.div>
      </AnimatePresence>
    </div>
  </SpotlightHeroAnimation>
</template>
